import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { inferenceService, UsageRequest } from '../services/inferenceApi';
import { walletApi } from '../services/platformApi';
import { useWalletModal } from '../store/walletModal';
import { groupByDay, isLowBalance } from './usage.format';
import { Page, PageHeader, Tag, InferenceTabs, INFERENCE_EYEBROW } from '../components/console/ui';

export default function Usage() {
  const showWallet = useWalletModal((s) => s.show);
  const balanceQ = useQuery({ queryKey: ['wallet'], queryFn: () => walletApi.getWallet().then((r) => r.data) });
  const summaryQ = useQuery({ queryKey: ['usage-summary'], queryFn: () => inferenceService.getUsageSummary().then((r) => r.data) });
  const requestsQ = useQuery({ queryKey: ['usage-requests'], queryFn: () => inferenceService.getUsageRequests({ limit: 100 }).then((r) => r.data) });

  const requests: UsageRequest[] = requestsQ.data?.data ?? [];
  const totalRequests = requestsQ.data?.total;
  const summary = summaryQ.data?.summary;
  const series = groupByDay(requests);
  const balance = balanceQ.data?.available_balance ?? balanceQ.data?.balance ?? 0;
  const peak = series.reduce<(typeof series)[number] | null>((best, p) => (!best || p.cost > best.cost ? p : best), null);

  return (
    <Page>
      <PageHeader
        eyebrow={INFERENCE_EYEBROW}
        title="Usage"
        subtitle="Your inference spend and request history."
        actions={
          <button type="button" className="ps-btn" onClick={showWallet}>
            Top up
          </button>
        }
      />

      <InferenceTabs />

      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,180px),1fr))]">
        <Stat label="Spend" value={summaryQ.isError ? '-' : `₹${(summary?.total_cost ?? 0).toFixed(2)}`} />
        <Stat label="Requests" value={summaryQ.isError ? '-' : (summary?.total_requests ?? 0).toLocaleString()} />
        <Stat label="Tokens" value={summaryQ.isError ? '-' : (summary?.total_tokens ?? 0).toLocaleString()} />
        <div className="ps-card flex flex-col gap-1.5 p-4">
          <span className="text-xs text-ps-subtle">Wallet balance</span>
          {balanceQ.isError ? (
            <span className="text-sm text-ps-err">Balance unavailable - please retry.</span>
          ) : (
            <span className="font-display text-[26px] font-medium tracking-[-0.01em]">₹{balance.toFixed(2)}</span>
          )}
          <button onClick={showWallet} className="ps-link self-start text-xs">
            Top up →
          </button>
        </div>
      </div>

      {summaryQ.isError && (
        <div className="ps-card border-ps-err/40 bg-ps-err-bg px-4 py-3 text-sm text-ps-err">
          Could not load your usage summary. The numbers above are unavailable, not zero - refresh to retry.
        </div>
      )}

      {balanceQ.data && isLowBalance(balance) && (
        <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[#5B2A0E] bg-[#140C06] px-3.5 py-2.5 text-[13px]">
          <Tag tone="warn">Low balance</Tag>
          <span className="text-ps-muted">
            Your balance is low.{' '}
            <button onClick={showWallet} className="ps-link underline">
              Top up your wallet
            </button>{' '}
            to avoid interrupted requests.
          </span>
        </div>
      )}

      <section className="ps-card flex flex-col gap-3 p-5" aria-labelledby="daily-spend">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="daily-spend" className="m-0 text-[17px] font-medium">
            Daily spend (₹)
          </h2>
          {peak && peak.cost > 0 && (
            <span className="text-xs text-ps-subtle">
              Peak ₹{peak.cost.toFixed(2)} on {peak.day}
            </span>
          )}
        </div>
        {series.length === 0 && !requestsQ.isLoading && (
          <p className="m-0 text-xs text-ps-subtle">No spend recorded yet.</p>
        )}
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={series} margin={{ top: 8, right: 0, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#1A1A24" />
            <XAxis dataKey="day" stroke="#8A8A93" fontSize={11} tickLine={false} axisLine={{ stroke: '#232330' }} />
            <YAxis stroke="#8A8A93" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: 'rgba(249,115,22,0.08)' }}
              contentStyle={{ background: '#1A1A24', border: '1px solid #34343F', borderRadius: 6, fontSize: 11, color: '#EDEDF0' }}
            />
            <Bar dataKey="cost" fill="#F97316" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="recent-requests">
        <h2 id="recent-requests" className="m-0 text-[17px] font-medium">
          Recent requests
        </h2>
        <div className="ps-card overflow-x-auto">
          {requestsQ.isLoading ? (
            <div className="p-5 text-sm text-ps-subtle">Loading…</div>
          ) : requestsQ.isError ? (
            <div className="py-8 text-center text-sm text-ps-err">Could not load request history - refresh to retry.</div>
          ) : requests.length === 0 ? (
            <div className="py-8 text-center text-sm text-ps-subtle">No requests yet.</div>
          ) : (
            <>
              <table className="ps-tbl min-w-[640px]">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Model</th>
                    <th className="!text-right">In</th>
                    <th className="!text-right">Out</th>
                    <th className="!text-right">Cost</th>
                    <th className="!text-right">Latency</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.request_id}>
                      <td className="text-ps-muted">{new Date(r.created_at).toLocaleString()}</td>
                      <td>{r.model_name}</td>
                      <td className="text-right">{r.prompt_tokens.toLocaleString()}</td>
                      <td className="text-right">{r.completion_tokens.toLocaleString()}</td>
                      <td className="text-right">₹{r.total_cost.toFixed(4)}</td>
                      <td className="text-right text-ps-muted">
                        {r.total_latency_ms != null ? `${r.total_latency_ms} ms` : '-'}
                      </td>
                      <td>
                        {r.cache_hit ? <Tag tone="accent">cached</Tag> : <Tag tone={statusTone(r.status)}>{r.status}</Tag>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3.5 py-2.5 text-xs text-ps-subtle">
                Showing {requests.length.toLocaleString()}
                {totalRequests != null && totalRequests > requests.length ? ` of ${totalRequests.toLocaleString()}` : ''}
              </div>
            </>
          )}
        </div>
      </section>
    </Page>
  );
}

function statusTone(status: string) {
  const s = (status || '').toLowerCase();
  if (['success', 'succeeded', 'completed', 'ok'].includes(s)) return 'ok' as const;
  if (['error', 'failed', 'timeout', 'timed_out'].includes(s)) return 'err' as const;
  return 'off' as const;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="ps-card flex flex-col gap-1.5 p-4">
      <span className="text-xs text-ps-subtle">{label}</span>
      <span className="font-display text-[26px] font-medium tracking-[-0.01em]">{value}</span>
    </div>
  );
}
